import { PinnedNavItem } from '@certhon/user-settings';
import { RestrictToVerticalAxis } from '@dnd-kit/abstract/modifiers';
import { move } from '@dnd-kit/helpers';
import { DragDropProvider } from '@dnd-kit/react';
import { useSortable } from '@dnd-kit/react/sortable';
import { GripVertical, Home } from 'lucide-react';
import { ReactNode, useEffect, useRef, useState } from 'react';

import { createLink } from '@/app/navigation/stack-links';
import { useIsPathActive } from '@/app/navigation/useActivePath';
import {
  navRowActionClass,
  NavRowContextMenu,
  PinToggleAction,
} from '@/components/Sidebar/nav-pin-controls';
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/Sidebar/sidebar';
import { getNavIcon } from '@/features/pinned-nav/pinned-nav.icons';
import { usePinnedNav } from '@/features/pinned-nav/pinned-nav.hooks';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

const SidebarMenuLink = createLink(SidebarMenuButton);

/**
 * User-pinned navigation entries, shown above the regular menu.
 *
 * The section disappears entirely when nothing is pinned. The first pin is the
 * user's home page (see `usePinnedNav`), marked with a small house glyph.
 *
 * Reordering is drag & drop on desktop and long-press → move up/down on touch,
 * where dragging would fight with scrolling the drawer.
 */
export function NavPinned() {
  const { pins } = usePinnedNav();
  const isMobile = useIsMobile();

  if (!pins.length) return null;

  return (
    <SidebarGroup>
      <SidebarGroupLabel>Pinned items</SidebarGroupLabel>
      <SidebarMenu>
        {isMobile ? (
          <TouchPinnedList pins={pins} />
        ) : (
          <SortablePinnedList pins={pins} />
        )}
      </SidebarMenu>
    </SidebarGroup>
  );
}

function TouchPinnedList({ pins }: { pins: PinnedNavItem[] }) {
  return (
    <>
      {pins.map((pin, index) => (
        <NavRowContextMenu
          key={pin.url}
          item={pin}
          pinnedIndex={index}
          pinnedCount={pins.length}
        >
          <PinnedRow pin={pin} index={index} />
        </NavRowContextMenu>
      ))}
    </>
  );
}

function SortablePinnedList({ pins }: { pins: PinnedNavItem[] }) {
  const { setPinOrder, canEdit } = usePinnedNav();

  // Order is local while dragging so rows can shuffle under the cursor; the
  // settings write only happens once, on drop.
  const [order, setOrder] = useState(() => pins.map((pin) => pin.url));
  // Read by onDragEnd, which fires after onDragOver has settled the order.
  // Synced in an effect rather than during render: a render that React throws
  // away must not leave the ref pointing at an order the user never dropped.
  const orderRef = useRef(order);
  useEffect(() => {
    orderRef.current = order;
  }, [order]);

  useEffect(() => {
    setOrder(pins.map((pin) => pin.url));
  }, [pins]);

  const byUrl = new Map(pins.map((pin) => [pin.url, pin]));

  return (
    <DragDropProvider
      onDragOver={(event) => setOrder((current) => move(current, event))}
      onDragEnd={(event) => {
        if (event.canceled) {
          setOrder(pins.map((pin) => pin.url));
          return;
        }
        setPinOrder(orderRef.current);
      }}
    >
      {order.map((url, index) => {
        const pin = byUrl.get(url);
        return pin ? (
          <SortablePinnedRow
            key={url}
            pin={pin}
            index={index}
            disabled={!canEdit}
          />
        ) : null;
      })}
    </DragDropProvider>
  );
}

function SortablePinnedRow({
  pin,
  index,
  disabled,
}: {
  pin: PinnedNavItem;
  index: number;
  /** Offline: the new order could not be saved. */
  disabled: boolean;
}) {
  const { ref, handleRef, isDragging } = useSortable({
    id: pin.url,
    index,
    disabled,
    // Without this the row follows the pointer sideways, and dragging past the
    // drawer's edge scrolls the whole sidebar.
    modifiers: [RestrictToVerticalAxis],
  });

  const label = disabled
    ? `Reorder ${pin.title} (offline)`
    : `Reorder ${pin.title}`;

  return (
    <PinnedRow
      pin={pin}
      index={index}
      ref={ref}
      className={cn(isDragging && 'z-10 opacity-80')}
      handle={
        <SidebarMenuAction
          ref={handleRef}
          showOnHover
          disabled={disabled}
          tabIndex={0}
          aria-roledescription="sortable"
          title={label}
          aria-label={label}
          className={cn(
            navRowActionClass,
            'right-7 cursor-grab active:cursor-grabbing',
            disabled && 'cursor-not-allowed',
          )}
        >
          <GripVertical />
        </SidebarMenuAction>
      }
    />
  );
}

function PinnedRow({
  pin,
  index,
  handle,
  // Anything else lands on the <li>: NavRowContextMenu clones this element to
  // attach its long-press handlers, and they have to reach a DOM node.
  ...liProps
}: React.ComponentProps<'li'> & {
  pin: PinnedNavItem;
  index: number;
  /** Drag handle, desktop only. */
  handle?: ReactNode;
}) {
  const isActive = useIsPathActive();
  const Icon = getNavIcon(pin.icon);
  const isHome = index === 0;

  return (
    <SidebarMenuItem {...liProps}>
      <SidebarMenuLink
        to={pin.url}
        tooltip={pin.title}
        isActive={isActive(pin.url)}
        className={handle ? 'md:pr-14' : undefined}
      >
        {/* Keep the icon column even without an icon, so labels stay aligned. */}
        {Icon ? <Icon /> : <span className="size-4 shrink-0" />}
        <span className="truncate">{pin.title}</span>
        {isHome && (
          // Trails the label rather than hugging the right edge, where it would
          // sit under the drag handle and the pin button.
          <Home
            className="text-muted-foreground size-3 shrink-0 opacity-70"
            aria-label="Home page"
          />
        )}
      </SidebarMenuLink>
      {handle}
      <PinToggleAction item={pin} />
    </SidebarMenuItem>
  );
}
